import { JSX, useMemo } from "react";

import { Box, Paper, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useHistoryChartQuery } from "src/api/locationChartQueries.api";
import CardHeader from "src/components/CardHeader/CardHeader";
import DifferenceChip from "src/components/Difference/DifferenceChip";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import sharedStyles from "src/constants/styles";
import { formatNum, getDifference, getTotalCurrentAndPastValues } from "src/lib/number.helpers";

import ExpectedVisitorsComponent from "./components/ExpectedVisitorsComponent";
import getStyles from "./helper/infoBar.helper";
import { type DashboardComponentParams } from "../interfaces/DashboardComponentParams";

function VisitorsMonthComponent({ selectedLocation }: DashboardComponentParams): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data: measuresData } = useHistoryChartQuery(selectedLocation?.Id, IntervalType.Month, null, MeasureType.Count);
  const chartData = useMemo(() => measuresData?.data ?? [], [measuresData?.data]);
  const diff = useMemo(() => getDifference(measuresData), [measuresData]);
  const dateTime = `${dayjs(measuresData?.currentDateRange.start).format("MMM, D")} - ${t("today")}`;
  const totalCurrentandPastValues = getTotalCurrentAndPastValues(chartData);
  const prevIsoYearDate = dayjs(measuresData?.pastDateRange.start);

  return (
    <Paper sx={getStyles(theme).fullheight}>
      <Box sx={getStyles(theme).innerBox}>
        <CardHeader header={t(`Visitors Month`)} dateTime={dateTime} />
        <Box sx={sharedStyles(theme).centerFlexbox} mt={3}>
          <Typography variant='h2'>{formatNum(totalCurrentandPastValues[0])}</Typography>
          <DifferenceChip difference={diff} fontSize={theme.fontSizes.md} />
        </Box>
        <Box sx={sharedStyles(theme).centerFlexbox} mt={1}>
          <Typography variant='body2' color={theme.palette.text.secondary}>
            {formatNum(totalCurrentandPastValues[1])} {t("previous year")}
          </Typography>
        </Box>

        <ExpectedVisitorsComponent
          date={prevIsoYearDate}
          intervalType={IntervalType.Month}
          locationId={selectedLocation.Id}
          chartData={chartData}
        />
      </Box>
      <Box />
    </Paper>
  );
}

export default VisitorsMonthComponent;
